import { CityAttractionRO } from '@escapenavigator/types/dist/city-attraction/city-attraction.ro';
import { AttractionCategoryEnum } from '@escapenavigator/types/dist/city-attraction/enum/attraction-category.enum';
import { AttractionFameEnum } from '@escapenavigator/types/dist/city-attraction/enum/attraction-fame.enum';
import { CreatePartnerAttractionDto } from '@escapenavigator/types/dist/partners-portal/create-partner-attraction.dto';
import { CreatePortalMaterialOrderDto } from '@escapenavigator/types/dist/partners-portal/create-portal-material-order.dto';
import {
    CreatePartnerCityQuestDto,
    UpdatePartnerCityQuestDto,
} from '@escapenavigator/types/dist/partners-portal/partner-city-quest.dto';
import { PartnerCityQuestRO } from '@escapenavigator/types/dist/partners-portal/partner-city-quest.ro';
import { PortalCityOptionRO } from '@escapenavigator/types/dist/partners-portal/portal-city-option.ro';
import { PortalMaterialOrderRO } from '@escapenavigator/types/dist/partners-portal/portal-material-order.ro';
import { PortalPartnerRO } from '@escapenavigator/types/dist/partners-portal/portal-partner.ro';
import { PortalPartnerStatsRO } from '@escapenavigator/types/dist/partners-portal/portal-stats.ro';
import { RegisterPortalPartnerDto } from '@escapenavigator/types/dist/partners-portal/register-portal-partner.dto';
import { UpdatePartnerAttractionDto } from '@escapenavigator/types/dist/partners-portal/update-partner-attraction.dto';
import { UpdatePortalPartnerDto } from '@escapenavigator/types/dist/partners-portal/update-portal-partner.dto';

import { apiClient } from './client';

type AuthResponse = {
    success: boolean;
    token?: string;
    emailVerified?: boolean;
    partner?: PortalPartnerRO;
};

export type AttractionsQuery = {
    cityId: number;
    searchText?: string;
    category?: AttractionCategoryEnum;
    fame?: AttractionFameEnum;
    district?: string;
};

export type AttractionDuplicatesQuery = {
    cityId: number;
    lat: number;
    lng: number;
    title?: string;
    excludeId?: number;
};

export const api = {
    register: (data: RegisterPortalPartnerDto) =>
        apiClient.post<{ success: boolean }>('/partners-portal/register', data),

    verifyEmail: (data: { email: string; code: string }) =>
        apiClient.post<AuthResponse>('/partners-portal/verify-email', data),

    resendCode: (email: string) =>
        apiClient.post<{ success: boolean }>('/partners-portal/resend-code', { email }),

    login: (data: { email: string; password: string }) =>
        apiClient.post<AuthResponse>('/partners-portal/login', data),

    logout: () => apiClient.get('/partners-portal/logout'),

    recover: (email: string) =>
        apiClient.post<{ success: boolean }>('/partners-portal/recover', { email }),

    resetPassword: (data: { email: string; code: string; password: string }) =>
        apiClient.post<AuthResponse>('/partners-portal/reset-password', data),

    me: () => apiClient.get<PortalPartnerRO>('/partners-portal/me'),

    updateMe: (data: UpdatePortalPartnerDto) =>
        apiClient.patch<PortalPartnerRO>('/partners-portal/me', data),

    cities: () => apiClient.get<PortalCityOptionRO[]>('/partners-portal/cities'),

    questCities: (search?: string) =>
        apiClient.get<PortalCityOptionRO[]>('/partners-portal/quest-cities', {
            params: search?.trim() ? { search: search.trim() } : undefined,
        }),

    /** @deprecated Use questCities */
    storyCities: (search?: string) =>
        apiClient.get<PortalCityOptionRO[]>('/partners-portal/quest-cities', {
            params: search?.trim() ? { search: search.trim() } : undefined,
        }),

    attractions: (query: AttractionsQuery) =>
        apiClient.get<CityAttractionRO[]>('/partners-portal/attractions', {
            params: {
                cityId: query.cityId,
                searchText: query.searchText || undefined,
                category: query.category || undefined,
                fame: query.fame || undefined,
                district: query.district || undefined,
            },
        }),

    attractionDuplicates: (query: AttractionDuplicatesQuery) =>
        apiClient.get<CityAttractionRO[]>('/partners-portal/attractions/duplicates', {
            params: {
                cityId: query.cityId,
                lat: query.lat,
                lng: query.lng,
                title: query.title || undefined,
                excludeId: query.excludeId || undefined,
            },
        }),

    createAttraction: (data: CreatePartnerAttractionDto) =>
        apiClient.post<CityAttractionRO>('/partners-portal/attractions', data),

    updateAttraction: (id: number, data: UpdatePartnerAttractionDto) =>
        apiClient.patch<CityAttractionRO>(`/partners-portal/attractions/${id}`, data),

    stats: () => apiClient.get<PortalPartnerStatsRO>('/partners-portal/stats'),

    quests: () => apiClient.get<PartnerCityQuestRO[]>('/partners-portal/quests'),

    quest: (id: number) => apiClient.get<PartnerCityQuestRO>(`/partners-portal/quests/${id}`),

    createQuest: (data: CreatePartnerCityQuestDto) =>
        apiClient.post<PartnerCityQuestRO>('/partners-portal/quests', data),

    updateQuest: (id: number, data: UpdatePartnerCityQuestDto) =>
        apiClient.patch<PartnerCityQuestRO>(`/partners-portal/quests/${id}`, data),

    deleteQuest: (id: number) => apiClient.delete(`/partners-portal/quests/${id}`),

    submitQuest: (id: number) =>
        apiClient.post<PartnerCityQuestRO>(`/partners-portal/quests/${id}/submit`),

    materialOrders: () =>
        apiClient.get<PortalMaterialOrderRO[]>('/partners-portal/material-orders'),

    createMaterialOrder: (data: CreatePortalMaterialOrderDto) =>
        apiClient.post<PortalMaterialOrderRO>('/partners-portal/material-orders', data),
};
